import type { ErrorView, QueryView } from '@krp/client-runtime'
import type { SelectOption } from '@krp/ui-kit'

import type { OwnPriceOwner } from '../api/contracts'

export type { OwnPriceOwner }
import type { ProductDraft } from './productForm'
import type { PriceHistoryRow } from './prices'

/**
 * Модели экранов своей номенклатуры (vm + actions): экран получает их пропсами и не знает ни store, ни HTTP. Собирает их
 * binding приложения (`apps/web/src/bindings/own`) из кэша запросов и намерения человека.
 */

/** Почему запись выключена: нет права на этом ресурсе. Кнопки видны всегда, причина написана рядом. */
export const CATALOG_WRITE_DISABLED = 'Нет права менять товары этой базы — его выдаёт администратор в «Доступе».'
export const PRICES_WRITE_DISABLED = 'Нет права менять цены этой базы — его выдаёт администратор в «Доступе».'

export interface ProductFilterDraft {
  sku: string
  barcode: string
  groupId: string
  showArchived: boolean
}

export const EMPTY_PRODUCT_FILTER: ProductFilterDraft = { sku: '', barcode: '', groupId: '', showArchived: false }

export interface ProductListRow {
  id: string
  name: string
  skuText: string
  barcodesText: string
  unitText: string
  groupText: string
  vatText: string
  archived: boolean
}

export interface OwnProductsVm {
  filter: ProductFilterDraft
  /** Что сейчас применено к списку — словами («артикул K-1, только действующие»). */
  appliedText: string
  groupOptions: readonly SelectOption[]
  query: QueryView
  rows: readonly ProductListRow[]
  pageNumber: number
  hasPrevious: boolean
  hasNext: boolean
  writeDisabledReason: string | null
}

export interface OwnProductsActions {
  filterChanged: (filter: ProductFilterDraft) => void
  apply: () => void
  reset: () => void
  next: () => void
  previous: () => void
  reload: () => void
  open: (productId: string) => void
  create: () => void
  openGroups: () => void
}

export interface ProductFact {
  label: string
  value: string
}

export interface CardPriceRow {
  key: string
  name: string
  priceText: string
  fromText: string
}

export type ProductCardMode = 'create' | 'view' | 'edit'

export interface OwnProductCardVm {
  mode: ProductCardMode
  /** Чтение карточки; у «Нового товара» — всегда `ready`. */
  query: QueryView
  title: string
  archived: boolean
  versionText: string
  facts: readonly ProductFact[]
  /** Чего не хватает для продажи: пусто — продать можно. */
  saleGaps: readonly string[]
  pricesOnText: string
  prices: readonly CardPriceRow[]
  draft: ProductDraft
  problem: string | null
  groupOptions: readonly SelectOption[]
  vatOptions: readonly SelectOption[]
  weightOptions: readonly SelectOption[]
  subjectOptions: readonly SelectOption[]
  methodOptions: readonly SelectOption[]
  saving: boolean
  /** Отказ записи — дословно, с кодом и номером запроса. */
  error: ErrorView | null
  done: string | null
  writeDisabledReason: string | null
  canSeePrices: boolean
}

export interface OwnProductCardActions {
  edit: () => void
  cancel: () => void
  draftChanged: (draft: ProductDraft) => void
  save: () => void
  archive: () => void
  restore: () => void
  reload: () => void
  openPrices: () => void
  close: () => void
}

export interface GroupDraft {
  id: string | null
  name: string
  parentId: string
  archived: boolean
}

export interface GroupListRow {
  id: string
  name: string
  path: string
  depth: number
  archived: boolean
}

export interface OwnGroupsVm {
  query: QueryView
  rows: readonly GroupListRow[]
  form: { draft: GroupDraft; parentOptions: readonly SelectOption[]; problem: string | null; saving: boolean; error: ErrorView | null } | null
  done: string | null
  writeDisabledReason: string | null
}

export interface OwnGroupsActions {
  create: () => void
  edit: (groupId: string) => void
  draftChanged: (draft: GroupDraft) => void
  save: () => void
  closeForm: () => void
  reload: () => void
  openProducts: () => void
}

export interface PriceKindDraft {
  id: string | null
  name: string
  currency: string
  archived: boolean
  owner: OwnPriceOwner
}

export interface PriceKindRow {
  id: string
  name: string
  currency: string
  archived: boolean
  owner: OwnPriceOwner
}

export type FinderMode = 'sku' | 'barcode'

export interface OwnPriceKindsVm {
  query: QueryView
  rows: readonly PriceKindRow[]
  form: { draft: PriceKindDraft; problem: string | null; saving: boolean; error: ErrorView | null } | null
  done: string | null
  writeDisabledReason: string | null
  finder: { text: string; mode: FinderMode; searched: boolean; query: QueryView; rows: readonly ProductListRow[] }
}

export interface OwnPriceKindsActions {
  create: () => void
  edit: (typeId: string) => void
  draftChanged: (draft: PriceKindDraft) => void
  save: () => void
  closeForm: () => void
  reload: () => void
  finderChanged: (text: string, mode: FinderMode) => void
  find: () => void
  openPrices: (productId: string) => void
  openProducts: () => void
}

export interface PriceSetDraft {
  priceTypeId: string
  validFrom: string
  price: string
}

export interface OwnProductPricesVm {
  productQuery: QueryView
  title: string
  skuText: string
  archived: boolean
  on: string
  onText: string
  current: { query: QueryView; rows: readonly PriceHistoryRow[] }
  history: { query: QueryView; rows: readonly PriceHistoryRow[] }
  kindOptions: readonly SelectOption[]
  form: { draft: PriceSetDraft; problem: string | null; saving: boolean; error: ErrorView | null }
  confirmDeleteKey: string | null
  deleting: boolean
  deleteError: ErrorView | null
  done: string | null
  writeDisabledReason: string | null
}

export interface OwnProductPricesActions {
  onChanged: (day: string) => void
  draftChanged: (draft: PriceSetDraft) => void
  save: () => void
  askDelete: (key: string) => void
  cancelDelete: () => void
  confirmDelete: () => void
  reload: () => void
  openProduct: () => void
}
